/* ==========================================================================
   Buena Vita — The Day

   A pinned, scroll-driven day: the canvas sky moves from dawn to night while
   the phase panels cross-fade, and each phase offers the products taken at
   that hour.

   Design notes worth keeping in mind before editing:

   * NOTHING HERE IS REQUIRED FOR THE SECTION TO WORK. The markup renders as a
     readable stack of blocks on its own. This file adds `.is-live`, and only
     after it has checked that pinning will actually work. If any check fails
     the shopper gets the stack, which is a perfectly good section.

   * STICKY DIES INSIDE `overflow: hidden`. An ancestor with hidden overflow
     becomes the nearest scrollport, and since that ancestor never scrolls, the
     sticky child never sticks. Elixir sets `overflow-x: hidden` in a couple of
     wrappers. `overflow: clip` clips identically WITHOUT creating a scrollport,
     so promoting hidden -> clip on our ancestors fixes the pin without
     changing what the page looks like.

   * THE SKY IS DRAWN AT ~1/6 SCALE and stretched up. That is the whole blur
     technique: no filters, no shaders, no libraries. It costs about a
     hundredth of the fill rate of drawing the same gradients full size, which
     is why this can run at 60fps on a phone.

   * PICKS ARE SHARED STATE. They read and write `bv:routine` — the same key
     the routine builder and goal finder use — and both directions are wired,
     so tapping here fills the builder below and vice versa.

   Depends on window.BVFX (bv-fx.js) for money formatting and the atomic
   multi-add; degrades to a plain link if it is missing.
   ========================================================================== */

(function () {
  'use strict';

  var KEY = 'bv:routine';
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- shared routine state ---------- */

  function read() {
    try {
      var v = JSON.parse(localStorage.getItem(KEY) || '[]');
      return Object.prototype.toString.call(v) === '[object Array]' ? v : [];
    } catch (err) {
      return [];
    }
  }

  function write(list) {
    try { localStorage.setItem(KEY, JSON.stringify(list)); } catch (err) { /* private mode */ }
    document.dispatchEvent(new CustomEvent('bv:routine:changed', { detail: { handles: list } }));
  }

  function money(cents) {
    if (window.BVFX && window.BVFX.money) return window.BVFX.money(cents);
    return '$' + (cents / 100).toFixed(2);
  }

  /* ---------- maths ---------- */

  function now() { return (window.performance && performance.now) ? performance.now() : Date.now(); }

  function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }
  function lerp(a, b, t) { return a + (b - a) * t; }

  function smooth(t) {
    t = clamp(t, 0, 1);
    return t * t * (3 - 2 * t);
  }

  /* Each phase gets a plateau: the first and last quarter of its scroll span
     are transition, the middle half is dwell. Without this the "phases" are
     just a continuous smear and no single moment ever looks composed. */
  function plateau(f) {
    return smooth(clamp((f - 0.25) / 0.5, 0, 1));
  }

  function hex(c) {
    c = String(c || '').trim();
    if (c.charAt(0) === '#') c = c.slice(1);
    if (c.length === 3) c = c[0] + c[0] + c[1] + c[1] + c[2] + c[2];
    var n = parseInt(c, 16);
    if (isNaN(n)) return [20, 24, 26];
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }

  function mix(a, b, t) {
    return [
      Math.round(lerp(a[0], b[0], t)),
      Math.round(lerp(a[1], b[1], t)),
      Math.round(lerp(a[2], b[2], t))
    ];
  }

  function rgba(c, a) { return 'rgba(' + c[0] + ',' + c[1] + ',' + c[2] + ',' + a + ')'; }

  /* The arc in the SVG is `M0,260 C250,-8 750,-8 1000,260` inside a 1000x260
     viewBox. These are the same control points normalised, so evaluating the
     cubic here puts the orb exactly on the drawn line at any aspect ratio —
     which is why the SVG can use preserveAspectRatio="none" without the orb
     drifting off it. */
  var P = [[0, 1], [0.25, -0.0308], [0.75, -0.0308], [1, 1]];

  function arcAt(t) {
    var u = 1 - t;
    var a = u * u * u, b = 3 * u * u * t, c = 3 * u * t * t, d = t * t * t;
    return {
      x: a * P[0][0] + b * P[1][0] + c * P[2][0] + d * P[3][0],
      y: a * P[0][1] + b * P[1][1] + c * P[2][1] + d * P[3][1]
    };
  }

  /* ---------- the sky ---------- */

  function Sky(canvas, phases) {
    var ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) return null;

    var off = document.createElement('canvas');
    var octx = off.getContext('2d', { alpha: false });
    var w = 0, h = 0, ow = 0, oh = 0;

    /* Deterministic star field. Math.random() per frame would make them
       shimmer like static; generated once, they just sit there like stars. */
    var stars = [];
    for (var i = 0; i < 70; i++) {
      var s = Math.sin(i * 12.9898) * 43758.5453;
      var s2 = Math.sin(i * 78.233) * 12345.6789;
      stars.push({
        x: s - Math.floor(s),
        y: (s2 - Math.floor(s2)) * 0.62,
        r: 0.4 + (s - Math.floor(s)) * 1.1,
        tw: i * 0.7
      });
    }

    function size() {
      var r = canvas.getBoundingClientRect();
      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = Math.max(1, Math.round(r.width * dpr));
      h = Math.max(1, Math.round(r.height * dpr));
      canvas.width = w;
      canvas.height = h;
      ow = Math.max(48, Math.round(r.width / 6));
      oh = Math.max(48, Math.round(r.height / 6));
      off.width = ow;
      off.height = oh;
      octx.imageSmoothingEnabled = true;
      ctx.imageSmoothingEnabled = true;
    }

    function blob(x, y, r, colour, alpha) {
      if (r <= 0 || alpha <= 0.002) return;
      var g = octx.createRadialGradient(x, y, 0, x, y, r);
      g.addColorStop(0, rgba(colour, alpha));
      g.addColorStop(0.55, rgba(colour, alpha * 0.42));
      g.addColorStop(1, rgba(colour, 0));
      octx.fillStyle = g;
      octx.beginPath();
      octx.arc(x, y, r, 0, Math.PI * 2);
      octx.fill();
    }

    function draw(state) {
      if (!w || !h) return;

      var i = state.index;
      var f = state.blend;
      var a = phases[i];
      var b = phases[Math.min(i + 1, phases.length - 1)];

      var deep = mix(a.deep, b.deep, f);
      var mid = mix(a.mid, b.mid, f);
      var lite = mix(a.lite, b.lite, f);

      var t = state.time;
      var pulse = state.pulse;
      var night = state.night;

      octx.fillStyle = 'rgb(' + deep[0] + ',' + deep[1] + ',' + deep[2] + ')';
      octx.fillRect(0, 0, ow, oh);

      /* The horizon: a wide, low band of the mid tone that anchors everything
         and stops the field reading as random lava-lamp blobs. */
      var horizon = octx.createLinearGradient(0, oh * 0.34, 0, oh);
      horizon.addColorStop(0, rgba(mid, 0));
      horizon.addColorStop(1, rgba(mid, 0.62 + pulse * 0.1));
      octx.fillStyle = horizon;
      octx.fillRect(0, 0, ow, oh);

      /* The light source rides the same arc the orb does, so the glow always
         comes from wherever the sun actually is. This is the detail that makes
         the whole thing read as a sky rather than as a gradient. */
      var sun = arcAt(state.sunT);
      var sx = sun.x * ow;
      var sy = sun.y * oh * 0.86 + oh * 0.06;

      blob(sx, sy, Math.max(ow, oh) * (0.55 + pulse * 0.08), lite, 0.72 + pulse * 0.16);
      blob(sx, sy, Math.max(ow, oh) * 0.2, lite, 0.5 + pulse * 0.2);

      /* Two slow drifters. Their only job is to keep the field alive when the
         page is not being scrolled. */
      blob(
        ow * (0.22 + Math.sin(t * 0.00021) * 0.1),
        oh * (0.62 + Math.cos(t * 0.00017) * 0.09),
        Math.max(ow, oh) * 0.42,
        mid,
        0.4
      );
      blob(
        ow * (0.8 + Math.cos(t * 0.00013 + 2) * 0.11),
        oh * (0.3 + Math.sin(t * 0.00019 + 1) * 0.1),
        Math.max(ow, oh) * 0.34,
        mid,
        0.32
      );

      ctx.drawImage(off, 0, 0, ow, oh, 0, 0, w, h);

      if (night > 0.02) {
        ctx.save();
        for (var k = 0; k < stars.length; k++) {
          var st = stars[k];
          var tw = 0.55 + Math.sin(t * 0.0011 + st.tw) * 0.45;
          ctx.globalAlpha = night * tw * 0.85;
          ctx.fillStyle = '#fff';
          ctx.beginPath();
          ctx.arc(st.x * w, st.y * h, st.r * (w / 900) * 1.6, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.restore();
      }
    }

    size();
    return { draw: draw, size: size };
  }

  /* ---------- section ---------- */

  function init(section) {
    if (section.dataset.bvRitualReady) return;
    section.dataset.bvRitualReady = '1';

    var stage = section.querySelector('[data-bv-stage]');
    var canvas = section.querySelector('[data-bv-sky]');
    var panes = [].slice.call(section.querySelectorAll('[data-bv-pane]'));
    var orb = section.querySelector('[data-bv-orb]');
    var orbShade = section.querySelector('[data-bv-orb-shade]');
    var nowEl = section.querySelector('[data-bv-now]');
    var nowLabel = section.querySelector('[data-bv-now-label]');
    var clock = section.querySelector('[data-bv-clock]');
    var clockText = section.querySelector('[data-bv-clock-text]');
    var jump = section.querySelector('[data-bv-jump]');
    var railDots = [].slice.call(section.querySelectorAll('[data-bv-rail-dot]'));
    var cue = section.querySelector('[data-bv-cue]');
    var countEl = section.querySelector('[data-bv-count]');
    var totalEl = section.querySelector('[data-bv-total]');
    var go = section.querySelector('[data-bv-go]');
    var picks = [].slice.call(section.querySelectorAll('[data-bv-pick]'));
    var emptyLabel = countEl ? countEl.textContent.trim() : '';

    if (!panes.length) return;

    var phases = panes.map(function (p) {
      var parts = String(p.dataset.sky || '').split(',');
      return {
        deep: hex(parts[0]),
        mid: hex(parts[1] || parts[0]),
        lite: hex(parts[2] || parts[1] || parts[0]),
        dark: p.dataset.ink === 'dark',
        label: p.dataset.label || '',
        start: parseFloat(p.dataset.hour),
        end: parseFloat(p.dataset.endHour)
      };
    });

    /* Picking something brightens the sky for about half a second. It is the
       only feedback in the section that is not a state change on the control
       itself, and it is what makes tapping through a phase feel like doing
       something to the world rather than ticking boxes. Declared up here
       because the pick handlers below run whether or not the stage goes
       live. */
    var pulseAt = -1e9;
    function pulseSky() { pulseAt = now(); }

    /* ---------- selection ---------- */

    function paint() {
      var chosen = read();
      var cents = 0;
      var mine = 0;

      picks.forEach(function (btn) {
        var on = chosen.indexOf(btn.dataset.handle) !== -1;
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        if (on) mine++;
      });

      /* Total covers the whole routine, not just what was tapped in here —
         the bar and the builder below must never disagree about the number. */
      var priced = {};
      picks.forEach(function (btn) { priced[btn.dataset.handle] = parseInt(btn.dataset.price, 10) || 0; });
      chosen.forEach(function (h) { cents += priced[h] || 0; });

      if (countEl) {
        countEl.textContent = chosen.length
          ? chosen.length + (chosen.length === 1 ? ' item in your day' : ' items in your day')
          : emptyLabel;
      }
      if (totalEl) {
        totalEl.hidden = !chosen.length;
        totalEl.textContent = cents ? money(cents) : '';
      }
      if (go) go.disabled = !chosen.length;

      return mine;
    }

    picks.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var chosen = read();
        var h = btn.dataset.handle;
        var at = chosen.indexOf(h);
        if (at === -1) { chosen.push(h); pulseSky(); } else { chosen.splice(at, 1); }
        write(chosen);
        paint();
      });
    });

    document.addEventListener('bv:routine:changed', function () { paint(); });
    paint();

    if (go) {
      go.addEventListener('click', function () {
        var chosen = read();
        if (!chosen.length) return;

        var byHandle = {};
        picks.forEach(function (b) { byHandle[b.dataset.handle] = b; });

        var items = [];
        chosen.forEach(function (h) {
          var b = byHandle[h];
          if (b && b.dataset.variant) items.push({ id: parseInt(b.dataset.variant, 10), quantity: 1 });
        });

        if (!items.length || !window.BVFX || !window.BVFX.addItems) {
          window.location.href = '/cart';
          return;
        }

        var label = go.textContent;
        go.disabled = true;
        go.textContent = 'Adding…';

        window.BVFX.addItems(items).then(function (res) {
          go.disabled = false;
          go.textContent = label;
          if (res && res.ok) {
            if (window.BVFX.toast) {
              window.BVFX.toast({
                title: 'Your day is in the cart',
                sub: items.length + (items.length === 1 ? ' item added' : ' items added')
              });
            }
          } else if (window.BVFX.toast) {
            window.BVFX.toast({
              title: 'Could not add that',
              sub: res ? res.message : '',
              error: true
            });
          }
        });
      });
    }

    /* ---------- go live? ---------- */

    /* The stage is the whole point of this section, so phones get it too — it
       is only ruled out where it genuinely cannot work: reduced motion, no
       canvas, a single phase, or a viewport with no room. "Room" is not a
       width breakpoint but an actual measurement taken below, because the
       thing that breaks a pinned stage is height, and a 390x844 phone has more
       usable height than a 1280x600 laptop window. */
    var tiny = window.matchMedia('(max-width: 379px)').matches;
    var canPin = !reduce && !tiny && !!canvas && !!canvas.getContext && panes.length > 1;

    if (!canPin) {
      /* Still worth telling people what time it is — it is the friendliest
         thing on the page — but nothing moves. */
      stamp();
      return;
    }

    /* Promote any hidden-overflow ancestor to clip, or the pin silently does
       nothing. Only touches the axis that is actually hidden. */
    if (window.CSS && CSS.supports && CSS.supports('overflow', 'clip')) {
      var node = section.parentElement;
      while (node && node !== document.body) {
        var cs = getComputedStyle(node);
        if (cs.overflowX === 'hidden') node.style.overflowX = 'clip';
        if (cs.overflowY === 'hidden') node.style.overflowY = 'clip';
        node = node.parentElement;
      }
    }

    var inner = section.querySelector('.bv-ritual__inner');

    /* Does the composed stage actually fit the viewport it has been given?
       The stage clips its overflow, so a stage that is one line too tall does
       not look cramped — it silently eats the "add to cart" bar. Rather than
       guess with breakpoints, go live, measure, and step back down to the
       stacked fallback if the content does not fit. This is re-run on resize
       and orientation change, so rotating a phone moves between the two
       layouts instead of committing to whichever was right at load. */
    function fits() {
      if (!inner || !stage) return false;
      return inner.scrollHeight <= stage.clientHeight - 6;
    }

    var sky = Sky(canvas, phases);
    if (!sky) {
      stamp();
      return;
    }

    var live = false;

    /* Add the class, measure, and take it back off if the content did not fit.
       Because the whole stage is built either way, this can move in both
       directions for the life of the page — rotate a phone and it swaps
       layouts rather than being stuck with whichever was right at load. */
    function applyMode() {
      section.classList.add('is-live');
      var ok = fits();

      if (!ok) {
        section.classList.remove('is-live');
        if (live) {
          live = false;
          stop();
          panes.forEach(function (p) {
            p.style.removeProperty('--o');
            p.style.removeProperty('--y');
            p.removeAttribute('data-off');
          });
          section.classList.remove('is-ink-dark');
        }
        return false;
      }

      if (!live) {
        live = true;
        measure();
        render(now());
        if (inView()) start();
      }
      return true;
    }

    function inView() {
      var r = section.getBoundingClientRect();
      return r.top < window.innerHeight && r.bottom > 0;
    }

    var state = {
      index: 0,
      blend: 0,
      time: 0,
      sunT: 0,
      night: 0,
      pulse: 0
    };

    var arcEl = section.querySelector('[data-bv-arc]');
    var arcBox = { w: 0, h: 0 };

    function measure() {
      sky.size();
      if (arcEl) {
        var r = arcEl.getBoundingClientRect();
        arcBox.w = r.width;
        arcBox.h = r.height;
      }
    }

    /* Deliberately not measured here: the canvas has no box until .is-live is
       on, so sizing happens inside applyMode() once the mode is settled. */

    var running = false;
    var raf = 0;

    /* render() does the work for exactly one frame and knows nothing about
       scheduling; frame() is the rAF wrapper. Keeping them apart is what lets
       start() and paint() draw synchronously without ever clobbering a pending
       rAF handle and leaving two loops running. */
    function render(now) {
      var rect = section.getBoundingClientRect();
      var span = section.offsetHeight - stage.offsetHeight;
      var p = span > 0 ? clamp(-rect.top / span, 0, 1) : 0;

      var n = panes.length;
      var x = p * (n - 1);
      var i = Math.min(Math.floor(x), n - 2);
      var f = plateau(x - i);

      state.index = i;
      state.blend = f;
      state.time = now;
      state.sunT = p;
      state.night = clamp((p - 0.62) / 0.38, 0, 1);
      state.pulse = clamp(1 - (now - pulseAt) / 700, 0, 1);
      state.pulse = state.pulse * state.pulse;

      /* Panes: the outgoing one leaves upward, the incoming one arrives from
         below. 26px is small on purpose — this is a cross-fade with a hint of
         travel, not a slide deck. */
      for (var k = 0; k < n; k++) {
        var o = 0, y = 0;
        if (k === i) { o = 1 - f; y = -f * 26; }
        else if (k === i + 1) { o = f; y = (1 - f) * 26; }
        var pane = panes[k];
        pane.style.setProperty('--o', o.toFixed(3));
        pane.style.setProperty('--y', y.toFixed(1) + 'px');
        if (o < 0.02) pane.setAttribute('data-off', ''); else pane.removeAttribute('data-off');
      }

      var dark = f < 0.5 ? phases[i].dark : phases[Math.min(i + 1, n - 1)].dark;
      section.classList.toggle('is-ink-dark', !!dark);

      /* Transform, not left/top: this runs every frame, and percentage offsets
         would force a layout pass on each one. The arc box is measured on
         resize instead. */
      var a = arcAt(p);
      if (orb) {
        orb.style.transform =
          'translate3d(' + (a.x * arcBox.w).toFixed(1) + 'px,' + (a.y * arcBox.h).toFixed(1) + 'px,0)';
      }
      if (orbShade) orbShade.style.opacity = state.night.toFixed(3);

      /* The cue has done its job the moment the shopper starts moving; fading
         it over the first 7% of the section is enough to register as "that
         went away because I scrolled" rather than as a flicker. */
      if (cue) cue.style.setProperty('--cue', clamp(1 - p / 0.07, 0, 1).toFixed(3));

      for (var d = 0; d < railDots.length; d++) {
        railDots[d].classList.toggle('is-on', d === (f < 0.5 ? i : Math.min(i + 1, n - 1)));
      }

      sky.draw(state);
    }

    function frame(ts) {
      raf = 0;
      if (!running) return;
      render(ts);
      raf = requestAnimationFrame(frame);
    }

    function start() {
      if (running) return;
      running = true;
      /* Paint one frame synchronously before handing over to rAF. Without this
         the panes sit at their initial --o of 0 until the first animation
         frame, which on a slow first load is a visible flash of empty stage —
         and in a backgrounded tab, where rAF never fires at all, it would stay
         empty forever. */
      render(now());
      raf = requestAnimationFrame(frame);
    }

    function stop() {
      running = false;
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
    }

    /* Exposed on the element so the theme editor can shut the loop down when
       it swaps the section out — otherwise every edit leaves another rAF loop
       running against a detached canvas. `paint` is the same single frame
       start() uses, and is what to call from the console when checking a
       specific scroll position. */
    section.bvRitual = {
      start: start,
      stop: stop,
      paint: function () { render(now()); }
    };

    /* Settle the mode and paint one composed frame before anything is
       observed. IntersectionObserver fires asynchronously, and on a slow load
       that gap is long enough to see an unpainted stage go by. */
    applyMode();

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (!live) return;
          if (e.isIntersecting) start(); else stop();
        });
      }, { rootMargin: '120px 0px' }).observe(section);
    } else if (live) {
      start();
    }

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stop();
      else if (live && inView()) start();
    });

    var resizeTimer = 0;
    function onResize() {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        if (applyMode() && live) measure();
      }, 150);
    }

    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);

    stamp();

    /* ---------- the clock ---------- */

    function stamp() {
      if (!clock || !clockText) return;

      /* Only the start of the day is needed now — the end was used by the old
         clock-scale mapping and no longer is. */
      var dayStart = isNaN(phases[0].start) ? 6 : phases[0].start;

      function tick() {
        var d = new Date();
        var hour = d.getHours() + d.getMinutes() / 60;

        /* Which phase owns this hour. The last phase deliberately owns
           everything after its start AND everything before the day begins —
           otherwise 2am falls through to the morning panel, and that is wrong
           for exactly the people awake to read it. */
        var idx = phases.length - 1;
        for (var k = 0; k < phases.length; k++) {
          var s = phases[k].start;
          var e = phases[k].end;
          if (isNaN(s) || isNaN(e)) continue;
          if (hour >= s && hour < e) { idx = k; break; }
        }
        if (hour < dayStart) idx = phases.length - 1;

        var time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
        clockText.innerHTML =
          'It’s <b>' + time + '</b> where you are — you’re in the <b>' +
          (phases[idx].label || 'day').toLowerCase() + '</b>.';
        clock.hidden = false;

        /* THE JUMP TARGET HAS TO BE ON THE SAME SCALE AS THE SCROLL, and the
           scroll runs on the PANE axis, not the clock. Pane i is fully
           composed at p = i / (n - 1), while hours are spread unevenly across
           the phases — morning is six hours, night is three. Mapping the hour
           linearly across the whole day therefore sent 10:12, which is
           squarely inside a 05:00–11:00 morning, to p = 0.27; and 0.27 of the
           pane axis is midday. That is the bug: the clock text said "morning"
           and the button scrolled past it.

           Derive it from the phase index instead, nudged by how far through
           that phase we are — but only by ±0.2 of a pane, because that is the
           width of the window where a pane holds full opacity. So "now" always
           lands on the phase the clock just named, and still drifts a little
           across it as the hours pass. */
        var ph = phases[idx];
        var frac = 0.5;
        if (!isNaN(ph.start) && !isNaN(ph.end) && ph.end > ph.start) {
          /* A phase that owns the small hours (21:00 onwards) sees 2am as
             hour 2, which is before its own start. Roll it forward a day so
             the fraction runs 0 → 1 across the phase rather than clamping. */
          var h = hour < ph.start ? hour + 24 : hour;
          frac = clamp((h - ph.start) / (ph.end - ph.start), 0, 1);
        }

        var np = phases.length > 1
          ? clamp(idx + (frac - 0.5) * 0.4, 0, phases.length - 1) / (phases.length - 1)
          : 0;

        if (nowEl && canPin) {
          var a = arcAt(np);
          nowEl.style.left = (a.x * 100) + '%';
          nowEl.style.top = (a.y * 100) + '%';
          nowLabel.textContent = 'now';
          nowEl.hidden = false;
        }

        return np;
      }

      var nowP = tick();
      setInterval(function () { nowP = tick(); }, 30000);

      if (jump) {
        jump.addEventListener('click', function () {
          if (!canPin) {
            var target = panes[Math.min(Math.round(nowP * (panes.length - 1)), panes.length - 1)];
            if (target) target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
            return;
          }
          var span = section.offsetHeight - stage.offsetHeight;
          var top = section.getBoundingClientRect().top + window.pageYOffset + span * nowP;
          window.scrollTo({ top: top, behavior: reduce ? 'auto' : 'smooth' });
        });
      }
    }
  }

  function boot() {
    [].slice.call(document.querySelectorAll('[data-bv-ritual]')).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Theme editor re-renders sections without a page load. */
  document.addEventListener('shopify:section:load', function (e) {
    var s = e.target.querySelector ? e.target.querySelector('[data-bv-ritual]') : null;
    if (s) init(s);
  });
})();
